import { forwardRef, Ref, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { AttachedCommunicationsPopup } from "@/Common/Components/AttachedCommunications/AttachedCommunicationsPopup";
import { PdfViewer } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DocumentFileAnnotations } from "@/Documents/Api/Dto/DocumentFileAnnotations";
import { GetDocumentAnnotationsResponse } from "@/Documents/Api/Dto/Response/GetDocumentAnnotationsResponse";
import { MedicalDocumentItem, MedicalDocumentItemDto } from "@/Documents/Models/Medical/MedicalDocumentItem";
import { MedicalDocumentTableItem } from "@/Documents/Models/Medical/MedicalDocumentTableItem";
import { PdfAnnotationAction } from "@/Documents/Models/UI/PdfAnnotationActions";
import { AnnotationHelper } from "./Annotations/AnnotationHelper";
import { createStampCrossCheckTool, StampCrossCheckTool } from "./Annotations/Tools/StampCrossCheckTool";
import { createStampTextTool, StampTextTool } from "./Annotations/Tools/StampTextTool";
import { PdfAnnotationToolbar } from "./PdfAnnotationToolbar";

import styles from "./PdfAnnotationEditor.module.scss";

import PopOutButton from "@/Common/Components/Buttons/_images/pop-out-btn.svg";
import ZoomInIcon from "@/Globals/_images/zoom-in.svg";
import ZoomOutIcon from "@/Globals/_images/zoom-out.svg";

const Core = globalThis.Core;
const PdfTronLicenseKey = process.env.REACT_APP_PDFTRON_KEY;

Core.setWorkerPath("./webviewer");
Core.disableEmbeddedJavaScript();
Core.Annotations.SelectionModel.selectionAccuracyPadding = 10;
Core.Tools.AnnotationSelectTool.setMinimumAnnotationSizeWhenResizing(() => ({ width: 10, height: 10 }));

const readonlyActions = [
    PdfAnnotationAction.ZoomIn,
    PdfAnnotationAction.ZoomOut,
    PdfAnnotationAction.Open
];

const webviewerToolsForSelection = [
    Core.Tools.ToolNames.EDIT,
    Core.Tools.ToolNames.FREETEXT,
    Core.Tools.ToolNames.FREEHAND,
    Core.Tools.ToolNames.FREEHAND_HIGHLIGHT
];

const zoomMin = 0.5;
const zoomMax = 1.5;
const freehandAnnotationDelay = 500; // delay in ms for the freehand strokes under which they are considered as a single annotation

interface PdfAnnotationEditorProps {
    /** Medical document info model. */
    medicalDocumentItem: MedicalDocumentItemDto;

    /** Is the selected file a Pdf file. */
    isSelectedFilePdf: boolean;

    /** Display signature annotation. */
    displaySignature: boolean;

    /** Enable signature annotation. */
    enableSignature: boolean;

    /** Selected file ID of the document. */
    selectedFileId: string;

    /** Whether to avoid setting the action to signature when the PIN is valid. */
    skipSettingActionToSignature?: boolean;

    /** Callback executed when the selected file changes. */
    onSelectedFileChange: (fileId: string) => void;

    /** Callback executed if an annotation is inserted or edited. */
    onEditedStatusChanged: (isEdited: boolean) => void;

    /** Callback to save annotations on the server */
    onSaveAnnotations: (reload: boolean) => Promise<void>;

    /** Callback executed when the displayed file is corrupted */
    onCorruptedFileDisplay: () => void;
}

interface PdfAnnotationEditorRef {
    /** Get annotations for all the document files annotations. */
    getFileAnnotations: () => Promise<DocumentFileAnnotations[]>;

    /** Reload annotations from server. */
    reloadAnnotations: () => Promise<void>;
}

function PdfAnnotationEditor(props: PdfAnnotationEditorProps, ref: Ref<PdfAnnotationEditorRef>): JSX.Element {
    const { getText } = useTranslation();

    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [scrollForEditMode, setScrollForEditMode] = useState<number>(null);
    const [zoom, setZoom] = useState<number>(getDefaultZoom());
    const [showTasks, setShowTasks] = useState<boolean>(false);
    const [medicalDocumentTableItem, setMedicalDocumentTableItem] = useState<MedicalDocumentTableItem>(new MedicalDocumentTableItem(new MedicalDocumentItem(props.medicalDocumentItem)));

    const [documentViewer, setDocumentViewer] = useState<Core.DocumentViewer>(null);
    const [documentViewverZoomRatio, setdocumentViewerZoomRatio] = useState<number>(1);
    const [annotationManager, setAnnotationManager] = useState<Core.AnnotationManager>(null);
    const [fileAnnotations, setFileAnnotations] = useState<DocumentFileAnnotations[]>([]);
    const [annotationsStateById, setAnnotationsStateById] = useState<Record<string, boolean>>({});
    const [selectedAction, setSelectedAction] = useState<PdfAnnotationAction>(PdfAnnotationAction.None);
    const [isFileCorrupted, setIsFileCorrupted] = useState<boolean>(false);

    const enableClearButton = useMemo(() => annotationsStateById[props.selectedFileId], [annotationsStateById, props.selectedFileId]);
    const helper = useMemo(() => new MyleHelper(), []);
    const readableFileName = useMemo(() => helper.GetReadableFileName(props.medicalDocumentItem.DocumentPathList.find(p => p.Id === props.selectedFileId)?.FileName ?? ""), [props.selectedFileId, helper, props.medicalDocumentItem]);
    const documentHeader = useMemo(
        () =>
            `${cacheStore.GetLocalizedValueByLookupId(props.medicalDocumentItem.ClassId)} -  ${cacheStore.GetLocalizedValueByLookupId(props.medicalDocumentItem.SubClassId)}${props.medicalDocumentItem.Subject ? `: ${props.medicalDocumentItem.Subject}` : ""}`,
        [props.medicalDocumentItem]
    );

    const fileUrl = useMemo(() => buildFileUrl(props.selectedFileId, readableFileName, false), [props.selectedFileId, readableFileName]);
    const fileAnnotatedUrl = useMemo(() => buildFileUrl(props.selectedFileId, readableFileName, true), [props.selectedFileId, readableFileName]);

    const viewer = useRef(null);
    const scrollView = useRef(null);
    const iframeViewer = useRef(null);
    const previousMedicalDocumentItemProp = useRef<MedicalDocumentItemDto>();
    const previousEnableSignatureProp = useRef<boolean>(false);
    const onAnnotationChangedRef = useRef(null);
    const onAnnotationLoadedRef = useRef(null);
    const onAnnotationSelectedRef = useRef(null);
    const documentAnnotationTaskIconRef = useRef(null);

    useImperativeHandle(
        ref,
        () => ({
            getFileAnnotations: saveAnnotations,
            reloadAnnotations: clearAnnotations
        })
    );

    // dispose viewver on unmount
    useEffect(() => () => {
        documentViewer?.dispose();

        // That effect should only run on unmount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // clean on new document
    useEffect(() => {
        if (previousMedicalDocumentItemProp.current?.Id === props.medicalDocumentItem.Id)
            return;

        documentViewer?.closeDocument();
        setReadOnly(true);
        setdocumentViewerZoomRatio(1);
        setSelectedAction(PdfAnnotationAction.None);
        setFileAnnotations([]);
        setAnnotationsStateById({});
        setIsFileCorrupted(false);

        // That effect should only run if the document change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.medicalDocumentItem]);

    // change document file
    useEffect(() => {
        if (documentViewer && !readOnly && fileUrl && previousMedicalDocumentItemProp.current?.Id === props.medicalDocumentItem.Id) {
            loadDocument();
        }

        // That effect should only run when:
        // - switching to edit mode
        // - when the document file change in edit mode
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.medicalDocumentItem, fileUrl, documentViewer, readOnly]);

    // clean on revoke approval
    useEffect(() => {
        if (
            previousMedicalDocumentItemProp.current?.Id === props.medicalDocumentItem.Id
            && !props.medicalDocumentItem.SignOffByContactId
            && previousMedicalDocumentItemProp.current?.SignOffByContactId !== props.medicalDocumentItem.SignOffByContactId
        ) {
            clearAnnotations();
        }

        // That effect should only run when the document approval is revoke
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.medicalDocumentItem, props.medicalDocumentItem.SignOffByContactId]);

    // save previous medicalDocumentItem
    useEffect(() => {
        previousMedicalDocumentItemProp.current = MyleHelper.CloneObject(props.medicalDocumentItem);
        setMedicalDocumentTableItem(new MedicalDocumentTableItem(new MedicalDocumentItem(props.medicalDocumentItem)));
    }, [props.medicalDocumentItem]);

    // set signature action when PIN is valid
    useEffect(
        () => {
            if (props.enableSignature && !previousEnableSignatureProp.current && !props.skipSettingActionToSignature) {
                setAction(PdfAnnotationAction.Signature);
            }
            previousEnableSignatureProp.current = props.enableSignature;
        },
        // That effect should only run when signature get enabled
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props, props.enableSignature, props.skipSettingActionToSignature]
    );

    async function loadDocument(): Promise<void> {
        const switchingFromReadonly = documentViewer.getDocument() === null;

        await documentViewer.loadDocument(fileUrl, { licenseKey: PdfTronLicenseKey });
        const annotations = await loadAnnotations();
        if (annotations && annotationManager)
            annotationManager.importAnnotations(annotations.AnnotationsXml);

        setupFormFieldAsReadOnly();
        setupAnnotationManagerEvents();

        // conserve zoom level from readonly mode to edit mode
        if (switchingFromReadonly) {
            // adapt zoom to new viewer.
            const firstPageWidth = documentViewer.getPageWidth(1);
            const newZoomRatio = 1000 / firstPageWidth;
            setdocumentViewerZoomRatio(newZoomRatio); // Pdf viewver default width is 1000px when PdfTron is dynamic.
            documentViewer.zoomTo(zoom * newZoomRatio);

            // conserve scroll from readonly mode to edit mode
            if (scrollForEditMode) {
                scrollView.current.scrollTop = scrollForEditMode;
                setScrollForEditMode(null);
            }
        }
        else {
            documentViewer.zoomTo(zoom * documentViewverZoomRatio);
        }
    }

    function setupFormFieldAsReadOnly(): void {
        if (!annotationManager)
            return;

        if (onAnnotationLoadedRef.current)
            documentViewer.removeEventListener("annotationsLoaded", onAnnotationLoadedRef.current);

        onAnnotationLoadedRef.current = () => {
            const setFormFieldToReadOnly = (field: Core.Annotations.Forms.Field): void => {
                field.widgets.map(annot => {
                    annot.fieldFlags.set("ReadOnly", true);
                });

                field.children.forEach(setFormFieldToReadOnly);
            };
            const fieldManager = annotationManager.getFieldManager();
            fieldManager.forEachField(setFormFieldToReadOnly);
        };

        documentViewer.addEventListener("annotationsLoaded", onAnnotationLoadedRef.current);
    }

    function setupAnnotationManagerEvents(): void {
        if (!annotationManager)
            return;

        if (onAnnotationChangedRef.current)
            annotationManager.removeEventListener("annotationChanged", onAnnotationChangedRef.current);

        onAnnotationChangedRef.current = (annotations: Core.Annotations.Annotation[], action, { imported }) => {
            if (imported)
                return;

            // update edition status when annotation is changed
            updateEditStatus(true);

            const firstAnnotation = annotations.firstOrDefault();

            // monitor text stamp resizes
            if (action === "modify" && firstAnnotation?.ToolName === "StampTextTool") {
                onStampTextResized(firstAnnotation);
            }

            // monitor cross and check stamp resizes
            if (action === "modify" && firstAnnotation?.ToolName === "StampCrossCheckTool") {
                onStampCrossCheckResized(firstAnnotation);
            }
        };

        annotationManager.addEventListener("annotationChanged", onAnnotationChangedRef.current);

        if (onAnnotationSelectedRef.current)
            annotationManager.removeEventListener("annotationSelected", onAnnotationSelectedRef.current);

        onAnnotationSelectedRef.current = (annotationList: Core.Annotations.Annotation[], action) => {
            if (action !== "selected")
                return;

            // ensure select compatible tool if an annotation is selected.
            if (!webviewerToolsForSelection.contains(documentViewer.getToolMode().name)) {
                setAction(PdfAnnotationAction.Select);
                annotationManager.selectAnnotations(annotationList);
            }

            // disable text annotations resizes
            for (const annotation of annotationList.filter(annotation => annotation.Subject === "FreeText"))
                annotation.NoResize = true;
        };

        annotationManager.addEventListener("annotationSelected", onAnnotationSelectedRef.current);
    }

    async function loadAnnotations(): Promise<DocumentFileAnnotations> {
        if (!annotationManager)
            return null;

        return (
            fileAnnotations.find(annotations => annotations.DocumentByteId === props.selectedFileId)
            ?? await loadAnnotationsFromServer()
        );
    }

    async function loadAnnotationsFromServer(): Promise<DocumentFileAnnotations> {
        const response = await Client.postAsync<GetDocumentAnnotationsResponse>(
            ServerOperations.Server.Myle_MedicalDocuments_GetAnnotations,
            {
                DocumentByteId: props.selectedFileId
            }
        );

        if (!response.isSuccess)
            return;

        const fileAnnotation: DocumentFileAnnotations = {
            AnnotationsXml: response.data.AnnotationsXml,
            DocumentByteId: props.selectedFileId,
            ParentDocumentAnnotationId: response.data.Id
        };

        // reset state
        const newFileAnnotations = fileAnnotations.filter(p => p.DocumentByteId !== props.selectedFileId);
        newFileAnnotations.push(fileAnnotation);
        setFileAnnotations(newFileAnnotations);

        return fileAnnotation;
    }

    async function clearAnnotations(): Promise<void> {
        const annotationsList = annotationManager?.getAnnotationsList();
        if (!annotationsList?.any())
            return;

        annotationManager.deleteAnnotations(annotationsList);
        const annotations = await loadAnnotationsFromServer();
        if (annotations)
            annotationManager.importAnnotations(annotations.AnnotationsXml);

        updateEditStatus(false);
    }

    function updateEditStatus(isSelectedFileEdited: boolean): void {
        props.onEditedStatusChanged(isSelectedFileEdited);

        setAnnotationsStateById({ ...annotationsStateById, [props.selectedFileId]: isSelectedFileEdited });
    }

    async function onStampTextResized(annotation: Core.Annotations.Annotation): Promise<void> {
        if (annotation?.ToolName !== "StampTextTool")
            return;

        await createStampTextTool(documentViewer, annotation.Width, annotation.Height);
    }

    async function onStampCrossCheckResized(annotation: Core.Annotations.Annotation): Promise<void> {
        if (annotation?.ToolName !== "StampCrossCheckTool")
            return;

        await createStampCrossCheckTool(documentViewer, annotation.Width, annotation.Height);
    }

    async function createViewerIfNotExist(): Promise<Core.DocumentViewer> {
        if (documentViewer)
            return documentViewer;

        const localDocumentViewer = new Core.DocumentViewer();
        localDocumentViewer.setScrollViewElement(scrollView.current);
        localDocumentViewer.setViewerElement(viewer.current);
        localDocumentViewer.enableAnnotations();
        localDocumentViewer.closeDocument();

        await AnnotationHelper.applyDocumentViewerCustomToolsAndStyles(localDocumentViewer);
        setDocumentViewer(localDocumentViewer);

        const localAnnotationManager = localDocumentViewer.getAnnotationManager();
        localAnnotationManager.setCurrentUser(sessionStore.userId);
        localAnnotationManager.promoteUserToAdmin();
        setAnnotationManager(localAnnotationManager);

        return localDocumentViewer;
    }

    async function setAction(action: PdfAnnotationAction): Promise<void> {
        let localDocumentViewer = documentViewer;

        // switch to editor if action is not supported by the readonly view.
        if (readOnly && !readonlyActions.contains(action)) {
            localDocumentViewer = await createViewerIfNotExist();
            const readDocumentScrollTop = scrollView.current.scrollTop;
            setScrollForEditMode(readDocumentScrollTop);
            setReadOnly(false);
        }

        if (action !== PdfAnnotationAction.Undo && action !== PdfAnnotationAction.Redo)
            setSelectedAction(action);

        switch (action) {
            case PdfAnnotationAction.Select:
                localDocumentViewer.setToolMode(localDocumentViewer.getTool(Core.Tools.ToolNames.EDIT));
                break;
            case PdfAnnotationAction.Text:
                localDocumentViewer.setToolMode(localDocumentViewer.getTool(Core.Tools.ToolNames.FREETEXT));
                break;
            case PdfAnnotationAction.Pencil:
                localDocumentViewer.setToolMode(localDocumentViewer.getTool(Core.Tools.ToolNames.FREEHAND));
                const freehandTool = localDocumentViewer.getTool(Core.Tools.ToolNames.FREEHAND) as Core.Tools.FreeHandCreateTool;
                freehandTool.createDelay = freehandAnnotationDelay;
                localDocumentViewer.setToolMode(freehandTool);
                break;
            case PdfAnnotationAction.Highlight:
                const freehandHighlightTool = localDocumentViewer.getTool(Core.Tools.ToolNames.FREEHAND_HIGHLIGHT) as Core.Tools.FreeHandHighlightCreateTool;
                freehandHighlightTool.createDelay = freehandAnnotationDelay;
                localDocumentViewer.setToolMode(freehandHighlightTool);
                break;
            case PdfAnnotationAction.Rectangle:
                localDocumentViewer.setToolMode(localDocumentViewer.getTool(Core.Tools.ToolNames.RECTANGLE));
                break;
            case PdfAnnotationAction.Eraser:
                localDocumentViewer.setToolMode(localDocumentViewer.getTool(Core.Tools.ToolNames.ERASER));
                break;
            case PdfAnnotationAction.Undo:
                localDocumentViewer?.getAnnotationHistoryManager().undo();
                break;
            case PdfAnnotationAction.Redo:
                localDocumentViewer?.getAnnotationHistoryManager().redo();
                break;
            case PdfAnnotationAction.Check:
            case PdfAnnotationAction.Cross:
                const crossCheckTool = localDocumentViewer.getTool("StampCrossCheckTool") as StampCrossCheckTool;
                crossCheckTool.setType(action);
                localDocumentViewer.setToolMode(crossCheckTool);
                break;
            case PdfAnnotationAction.R3:
            case PdfAnnotationAction.R6:
            case PdfAnnotationAction.R12:
            case PdfAnnotationAction.R24:
                const stampTextTool = localDocumentViewer.getTool("StampTextTool") as StampTextTool;
                stampTextTool.setText(PdfAnnotationAction[action].toString());
                localDocumentViewer.setToolMode(stampTextTool);
                break;
            case PdfAnnotationAction.Signature:
                localDocumentViewer.setToolMode(localDocumentViewer.getTool("SignatureTool"));
                break;
            case PdfAnnotationAction.Clear:
                annotationManager.deleteAnnotations(annotationManager.getAnnotationsList());
                const annotations = await loadAnnotationsFromServer();
                if (annotations)
                    annotationManager.importAnnotations(annotations.AnnotationsXml);
                updateEditStatus(false);
                break;
            case PdfAnnotationAction.ZoomIn:
            case PdfAnnotationAction.ZoomOut:
                const newZoom = zoom + (action === PdfAnnotationAction.ZoomIn ? 0.20 : -0.20);
                if (newZoom > zoomMin && newZoom < zoomMax) {
                    if (!props.isSelectedFilePdf)
                        iframeViewer.current.contentWindow.document.body.style.transform = `scale(${newZoom})`;

                    if (!readOnly)
                        localDocumentViewer.zoomTo(newZoom * documentViewverZoomRatio);

                    setZoom(newZoom);
                }
                break;
            case PdfAnnotationAction.Open:
                let docUrl = props.medicalDocumentItem.IsAnnotated
                    ? fileAnnotatedUrl
                    : fileUrl;

                if (!readOnly) {
                    await props.onSaveAnnotations(true);

                    // rebuild url to reset cache
                    docUrl = buildFileUrl(props.selectedFileId, readableFileName, true);
                }

                MyleHelper.OpenNewWindow(docUrl);
                break;
            default:
                console.error("The PDF annotation action is not supported.");
        }
    }

    async function onChangeSelectedFileId(newSelectedFileId: string): Promise<void> {
        await saveAnnotations();
        setIsFileCorrupted(false);
        props.onSelectedFileChange(newSelectedFileId);
    }

    async function saveAnnotations(): Promise<DocumentFileAnnotations[]> {
        if (readOnly)
            return;

        const annotations = await annotationManager.exportAnnotations();
        const newFileAnnotations = [...fileAnnotations];
        const fileAnnotation = newFileAnnotations.find(dto => dto.DocumentByteId === props.selectedFileId);

        if (fileAnnotation)
            fileAnnotation.AnnotationsXml = annotations;
        else {
            if (annotations.contains("<annots>"))
                newFileAnnotations.push({
                    DocumentByteId: props.selectedFileId,
                    AnnotationsXml: annotations
                });
        }
        setFileAnnotations(newFileAnnotations);
        return newFileAnnotations;
    }

    function showTaskWindow(): void {
        sessionStore.dispatch(LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup, {
            isTask: true,
            document: medicalDocumentTableItem
        });
    }

    function openNotes(e: React.MouseEvent): void {
        sessionStore.dispatch(LocalOperations.Documents.Note_Load, {
            item: medicalDocumentTableItem,
            event: e
        });
    }

    function setIframeViewerInnerStyles(iframe: HTMLIFrameElement, zoom: number): void {
        const bodyStyle = iframe.contentWindow.document.body.style;
        bodyStyle.height = "auto";
        bodyStyle.display = "flex";
        bodyStyle.justifyContent = "center";
        bodyStyle.transform = `scale(${zoom})`;
        iframe.style.visibility = "visible";
    }

    function taskRedirectClick(attachedCommunication: AttachedCommunication): void {
        sessionStore.dispatch(
            LocalOperations.Documents.Search_AttachedCommunicationClick,
            {
                attachedCommunication: attachedCommunication
            }
        );
    }

    function onLoadDocumentFailCallback(errorName?: string, errorMessage?: string): void {
        if (errorName && (errorName === "UnknownErrorException" || errorMessage?.contains("Illegal character"))) {
            // sets the file as corrupted if it's an unknown exception or it contains illegal characters and block annotation mode
            setIsFileCorrupted(true);
            sessionStore.showPopup(getText("DocumentAnnotation_CannotDisplayDocumentError"));
            props.onCorruptedFileDisplay();
        }
        else
            // switch to annotation mode when loading the document in the viewer fails
            setAction(PdfAnnotationAction.Select);
    }

    function handleShowTasks(): void {
        setShowTasks(true);

        sessionStore.dispatch(
            LocalOperations.Documents.Search_TrailClick,
            {
                attachedCommunications: medicalDocumentTableItem.Model.Model.IndividualTaskLinkedItems
            }
        );
    }

    const notesLength = props.medicalDocumentItem.NoteInfoCollection.length;
    const notesIndicatorText = ` ${notesLength || "+"} `;
    const notesIndicatorCss = `bold-text${(notesLength || props.medicalDocumentItem.IsUrgent ? " red" : "")}`;

    return (
        <>
            {showTasks && (
                <AttachedCommunicationsPopup
                    controlId="attached_communication_documentAnnotation"
                    topPosition={documentAnnotationTaskIconRef.current.getBoundingClientRect().top + 10}
                    leftPosition={documentAnnotationTaskIconRef.current.getBoundingClientRect().left - 590}
                    attachedCommunications={documentsStore.DocumentsModel.get("AttachedCommunicationsPopupList")}
                    context={AttachedCommunicationType.Task}
                    handleRedirect={task => taskRedirectClick(task)}
                    handleClose={() => setShowTasks(false)}
                />
            )}
            <h3 className={`${styles.title} thin-font`}>
                <span title={documentHeader}>{documentHeader}</span>
                <span data-cy="documentAnnotationEditor-header-walkmePlaceHolder" />
            </h3>
            <div className={styles.annotationMenu}>
                <PdfAnnotationToolbar
                    activeTool={selectedAction}
                    onActionClick={action => setAction(action)}
                    enableEdition={props.isSelectedFilePdf && !isFileCorrupted}
                    displaySignature={props.displaySignature}
                    enableSignature={props.enableSignature}
                    enableClearButton={enableClearButton}
                    disableUndoRedoButton={readOnly}
                    selectedFileId={props.selectedFileId}
                    onActionSettingsChanged={style => documentViewer.getTool(AnnotationHelper.getPdfTronToolName(selectedAction)).setStyles(style)}
                    annotationManager={annotationManager}
                />
            </div>
            <div className={styles.annotationWorkspace}>
                <div className={styles.documentHeader}>
                    <div className={styles.documentViewingTools}>
                        <div>
                            {!isFileCorrupted && (
                                <>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setAction(PdfAnnotationAction.ZoomOut)}
                                        disabled={zoom <= zoomMin}
                                        title={getText("DocumentAnnotation_ZoomOut")}
                                    >
                                        <ZoomOutIcon />
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setAction(PdfAnnotationAction.ZoomIn)}
                                        disabled={zoom >= zoomMax}
                                        title={getText("DocumentAnnotation_ZoomIn")}
                                    >
                                        <ZoomInIcon />
                                    </button>
                                </>
                            )}
                            <button
                                className="btn btn-primary"
                                onClick={() => setAction(PdfAnnotationAction.Open)}
                                title={getText("DocumentAnnotation_Popup")}
                            >
                                <PopOutButton />
                            </button>
                        </div>
                        {props.medicalDocumentItem.DocumentPathList.length > 1 && (
                            <div className={styles.attachments}>
                                <label className={styles.fontWeightBold}>{getText("AttachmentsLabel")}</label>
                                <select
                                    className="form-control myle-control"
                                    value={props.selectedFileId}
                                    onChange={e => onChangeSelectedFileId(e.target.value)}
                                >
                                    {props.medicalDocumentItem.DocumentPathList && props.medicalDocumentItem.DocumentPathList.map(doc => (
                                        <option key={doc.Id} value={doc.Id}>
                                            {MyleHelper.TruncateFileName(helper.GetReadableFileName(doc.FileName), 20, true)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    {props.medicalDocumentItem?.SenderFaxNumber && (
                        <div>
                            <label className={styles.fontWeightBold}>{getText("FaxFromLabel")}</label>
                            {`${props.medicalDocumentItem?.SenderFaxSource ?? ""} (${props.medicalDocumentItem.SenderFaxNumber})`}
                        </div>
                    )}
                    <div>
                        <a onClick={openNotes}>
                            [<span className={notesIndicatorCss}>{notesIndicatorText}</span>] {getText("DocumentDetailsLabel")}
                        </a>
                        <span>|</span>
                        <span>
                            <a onClick={showTaskWindow}>{getText("NewTaskLabel")}</a>
                            {props.medicalDocumentItem.IndividualTaskLinkedItems.any() && (
                                <span
                                    onClick={handleShowTasks}
                                    className={`${styles.newTaskIcon} ${props.medicalDocumentItem.AreAllTasksCompleted ? "circle-green" : "circle-orange"}`}
                                    ref={documentAnnotationTaskIconRef}
                                >
                                    {props.medicalDocumentItem.IndividualTaskLinkedItems.length}
                                </span>
                            )}
                        </span>
                    </div>
                </div>

                <div id="documentView" className={styles.documentView} ref={scrollView}>
                    {(!props.isSelectedFilePdf || (props.isSelectedFilePdf && isFileCorrupted))
                        ? (
                            <div className={styles.notPdfViewver}>
                                <iframe
                                    src={fileUrl}
                                    ref={iframeViewer}
                                    onLoad={iframe => setIframeViewerInnerStyles(iframe.currentTarget, zoom)}
                                />
                            </div>
                        )
                        : readOnly && (
                            <div className={styles.readOnlyViewer}>
                                <PdfViewer
                                    pdfSource={fileAnnotatedUrl}
                                    isReadOnly
                                    hideToolbar={true}
                                    selectedZoomRatio={zoom}
                                    onLoadDocumentFail={(errorName: string, errorMessage: string) => onLoadDocumentFailCallback(errorName, errorMessage)}
                                />
                            </div>
                        )
                    }
                    {/** The PDFTron viewer is hidden instead of unmouted in order to avoid re-instanciate it at will, improve performances */}
                    <div className={`${styles.viewer} ${(!props.isSelectedFilePdf || readOnly) ? styles.hidden : ""}`} ref={viewer} />
                </div>
            </div>
        </>
    );
}

function getDefaultZoom(): number {
    const screenSize = window.screen.width;
    if (screenSize <= 1200)
        return 0.6;
    else if (screenSize <= 1440)
        return 0.8;
    else
        return 1;
}

function buildFileUrl(documentByteId: string, readableFileName: string, includeAnnotations: boolean): string {
    return apiSettings.buildAppUrl(
        `api/medicalDocuments/downloadFile/${documentByteId}/${encodeURIComponent(readableFileName)}`,
        {
            auth: true,
            params: {
                IgnoreAnnotations: !includeAnnotations
            },
            noCache: includeAnnotations
        }
    );
}

const forwardedRef = forwardRef(PdfAnnotationEditor);

export {
    forwardedRef as PdfAnnotationEditor,
    PdfAnnotationEditorRef
};
